import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { useEffect, useRef, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { TreemapChart } from 'echarts/charts';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import type { EChartsOption } from 'echarts';
import type ReactEChartsCoreType from 'echarts-for-react/lib/core';
import {
  GridComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { breakpointsMediaQuery, getColor, useMediaQuery } from '@/helpers/utils';
import { useGetKeywordsMapQuery, useGetTariffInfoQuery } from '@/app/KeywordTracker/api/keywordsApi';
import { useOutletContext } from 'react-router-dom';
import chroma from 'chroma-js';
import TimePickerSpecific from '@/app/KeywordTracker/components/common/TimePickerSpecific';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import CardChartDropDown from '@/components/common/CardChartDropDown';
import TrackerKeywordsInactiveAlert from '@/app/KeywordTracker/components/TrackerKeywordsInactiveAlert';
import IconAlert from '../../../components/common/IconAlert';
import numberHumanizer from '@/helpers/numberHumanizer';
import negativeFixer from '../../../helpers/negativeFixer';
import useDomain from '@/hooks/useDomain';
import { useTooltipHide } from '@/hooks/useTooltipHide';
import { isTrackerKeywordsTariffZero } from '@/app/KeywordTracker/utils/trackerTariff';
import TrackerVideoPreload from '@/app/KeywordTracker/components/common/TrackerVideoPreload';
import type { OutletContext } from './pageTypes';
import DataLoadError from '@/components/errors/DataLoadError';
import {
  shouldShowDataLoadError,
  isNetworkApiError
} from '@/app/common/utils/apiErrorUtils';

type MapNode = {
  name: string;
  itemStyle: { color: string };
  growth: number;
  keyword: string;
  rank: number;
  value: number;
};

type TooltipParams = {
  data?: {
    children?: unknown;
    name?: string;
    value?: number;
    rank?: number;
    growth?: number;
  };
};

type LabelParams = {
  name?: string;
  data?: {
    name?: string;
    growth?: number;
  };
};

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  TreemapChart,
  CanvasRenderer
]);
const Map = () => {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  const [updateTime, setUpdateTime] = useState<number | null>(null);
  useEffect(() => {
    setBreadcrumbData([{ text: 'نقشه کلمات', href: '#' }]);
  }, [setBreadcrumbData]);
  const greenColors = chroma
    .scale([
      chroma(getColor('success')).brighten(1),
      chroma(getColor('success')).darken(1)
    ])
    .mode('lab')
    .colors(51);

  const redColors = chroma
    .scale([
      chroma(getColor('danger')).brighten(1),
      chroma(getColor('danger')).darken(1)
    ])
    .mode('lab')
    .colors(51);

  const { domain_slug } = useDomain();
  const { data: trackerInfo } = useGetTariffInfoQuery(domain_slug);
  const { data, isLoading, isFetching, isError, error, refetch } =
    useGetKeywordsMapQuery({
      domain_slug,
      updated_at: Number(updateTime ? updateTime : null)
    });

  useEffect(() => {
    if (!isLoading && data) {
      setUpdateTime(
        data?.data?.update_dates?.length > 0
          ? data?.data?.update_dates?.at(0) ?? null
          : null
      );
    }
  }, [data, isLoading]);
  const nodes: MapNode[] | undefined = data?.data?.keywords?.map(node => {
    return {
      name: `   ${node.keyword}   `,
      itemStyle: {
        color:
          node.growth === 0
            ? getColor('gray-100')
            : node.growth > 0
              ? greenColors[
                  Math.floor((node.growth / 100) * 50) > 50
                    ? 50
                    : Math.floor((node.growth / 100) * 50)
                ]
              : redColors[
                  Math.floor((-node.growth / 100) * 50) > 50
                    ? 50
                    : Math.floor((-node.growth / 100) * 50)
                ]
      },
      growth: node.growth,
      keyword: node.keyword,
      rank: node.rank,
      value: node.search_volume_miztols
    };
  });

  const chartRef = useRef<ReactEChartsCoreType | null>(null);
  useTooltipHide(chartRef);

  const cardRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<HTMLDivElement>(null);
  return (
    <Row>
      <Row className="g-0 my-3 p-0 px-3 justify-content-between">
        <Col sm="auto"></Col>

        <Col md="auto" className="mb-3 mb-md-0">
          <Row className="g-2 gy-md-0 h-100 align-items-center">
            <Col xs="auto">
              <h6 className="text-700 mb-0 text-nowrap">بازه زمانی</h6>
            </Col>
            <Col xs={12} md="auto">
              {data && !isFetching ? (
                <TimePickerSpecific
                  time={updateTime}
                  setTime={setUpdateTime}
                  availableDays={data?.data?.update_dates}
                />
              ) : (
                <TextLoading
                  width={isMdUp ? 205 : 100}
                  height={24}
                  isWidthPx={isMdUp}
                />
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      <Col xxl={12}>
        {shouldShowDataLoadError(isError, error) ? (
          <DataLoadError
            error={error}
            onRetry={() => refetch()}
            isRetrying={isFetching}
            isNetworkError={isNetworkApiError(error)}
          />
        ) : data && !isFetching ? (
          data?.data?.keywords?.length > 0 ? (
            <Card className="my-2" ref={cardRef}>
              <FalconCardHeader
                title={
                  <div>
                    نقشه کلمات{' '}
                    <HelpTooltip
                      html={
                        <div className="text-start">
                          با استفاده از تب "نقشه کلمات" می‌تونید خیلی سریع و
                          راحت، بدون نیاز به صرف زمان برای تحلیل، وضعیت سایتتون
                          رو در کلمات کلیدی که جستجوی بالا یا پایین دارن، متوجه
                          بشید. یعنی می‌تونید متوجه بشید که کدوم کلمات بیشترین
                          یا کمترین رشد و افت رو داشتن. برای اطلاعات بیشتر و
                          تحلیل‌های دقیق‌تر، می‌تونید از{' '}
                          <a
                            target="_blank"
                            rel="noreferrer noopener"
                            href="https://help.mizfa.tools/word-map-tab/"
                          >
                            {' '}
                            این لینک{' '}
                          </a>{' '}
                          استفاده کنید.
                        </div>
                      }
                    />
                  </div>
                }
                titleTag="h6"
                className="py-2"
                light
                endEl={
                  <CardChartDropDown cardRef={cardRef} chartRef={chartRef} />
                }
              />
              <Card.Body ref={svgRef} className="">
                {data && (
                  <ReactEChartsCore
                    ref={chartRef}
                    echarts={echarts}
                    option={getOptions(nodes, isFetching, isMdUp)}
                    style={{ height: '500px', width: '100%' }}
                  />
                )}
              </Card.Body>
            </Card>
          ) : (
            <div>
              {isTrackerKeywordsTariffZero(trackerInfo) ? (
                <TrackerKeywordsInactiveAlert />
              ) : (
                <IconAlert variant="warning">
                  زمانی برای شما نمودار نقشه کلمات نمایش داده میشود که حداقل ۲ بار
                  (روز دوم می‌تونید به‌روزسانی رو بزنید) روی دکمه به‌روزرسانی
                  کلیک کرده باشید.
                </IconAlert>
              )}
              <TrackerVideoPreload
                src="https://help.mizfa.tools/wp-content/uploads/2024/12/keyword-tracker-map-1.mp4"
                autoPlay
                loop
                muted
              />
            </div>
          )
        ) : (
          <TextLoading width={100} height={585} />
        )}
      </Col>
    </Row>
  );
};

export default Map;

const getOptions = (
  data: MapNode[] | undefined,
  isLoading: boolean,
  isMdUp: boolean
): EChartsOption => ({
  textStyle: {
    fontFamily: 'dana',
    color: getColor('gray-1100')
  },
  // نحوه نمایش تولتیپ نمودار
  tooltip: {
    triggerOn: isMdUp ? 'mousemove' : 'click',
    trigger: 'item',
    padding: [7, 10],
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-300'),
    textStyle: { color: getColor('gray-1100'), fontSize: 14 },
    borderWidth: 1,
    formatter: (params: TooltipParams) => {
      return params?.data?.children || isLoading || !params.data
        ? ``
        : `<div>
          <div style="direction:rtl;" class="d-flex gap-1 justify-content-start" >
          <span class="fw-bolder">
          ${params?.data?.name} <br/>
          </span>
          </div>
          <div class="text-700 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between w-100 gap-1">
          <span class="fw-bolder">
          میزان جستجو:
          </span>
          ${params?.data?.value === 1 ? 0 : numberHumanizer(params?.data?.value)} <br/>
          </div>
          <div class="text-700 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-start w-100 gap-1">
          <span class="fw-bolder">
          رتبه کلمه:
          </span>
          ${params?.data?.rank === 999 ? 'بدون رتبه' : numberHumanizer(params?.data?.rank)} <br/>
          </div>
          <div class="text-700 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-start w-100 gap-1">
          <span class="fw-bolder">
          میزان رشد/افت:
          </span>
          ${params?.data?.growth > 100 ? 'ورود' : params?.data?.growth < -100 ? 'خروج' : numberHumanizer(negativeFixer(params?.data?.growth))} <br/>
          </div>
          </div>          
          `?.toPersianDigit();
    },
    transitionDuration: 0,
    axisPointer: {
      type: 'none'
    }
  },
  grid: {
    left: 0,
    top: 0,
    right: 0,
    bottom: 0
  },
  series: [
    {
      type: 'treemap',
      data: data,
      breadcrumb: {
        // itemStyle: {
        //   textStyle: {
        //     padding: [10]
        //   }
        // }
      },
      width: '100%',
      height: '100%',
      label: {
        color: getColor('gray-1000'),
        formatter: (params: LabelParams) => {
          return params.name
            ? `${params?.data?.name} \n\n ${params?.data?.growth > 100 ? 'ورود' : params?.data?.growth < -100 ? 'خروج' : numberHumanizer(negativeFixer(params?.data?.growth))}`?.toPersianDigit()
            : 'بدون نام';
        }
      },
      levels: [
        {
          colorMappingBy: 'value',
          itemStyle: {
            borderWidth: 3,
            borderColor: '#333',
            gapWidth: 3
          }
        }
      ]
    }
  ]
}) as EChartsOption;
